import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Archive, ChevronRight, FileText, Ticket, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { VerificationBadge, EvidenceDrawer, RevisionMeta, CitationList, CiteThis } from "@/components/shared";

export const ArtifactsIndexPage = () => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get("/artifacts").then(res => {
      setArtifacts(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  
  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }
  
  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="artifacts-index-page">
      <h1 className="text-3xl font-bold serif-heading mb-4">Artifact Archive</h1>
      <p className="text-gray-600 mb-8">
        Original artifacts directly related to Aloha from Hawaii 1973, with verification status and provenance.
      </p>
      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {artifacts.map((artifact) => (
          <Link 
            key={artifact.id} 
            to={`/artifacts/${artifact.slug}`}
            className="archive-card overflow-hidden group"
            data-testid={`artifact-card-${artifact.slug}`}
          >
            <div className="h-48 bg-gray-100 flex items-center justify-center">
              <Archive className="w-12 h-12 text-gray-300" />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold serif-heading group-hover:text-blue-600 transition-colors">
                  {artifact.name}
                </h3>
                <VerificationBadge status={artifact.verification_status} />
              </div>
              {artifact.category && (
                <Badge variant="outline" className="text-xs mb-2">{artifact.category}</Badge>
              )}
              <p className="text-sm text-gray-600 line-clamp-2">{artifact.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const ArtifactDetailPage = () => {
  const { slug } = useParams();
  const [artifact, setArtifact] = useState(null);
  const [citations, setCitations] = useState([]);
  const [ticketEntries, setTicketEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await api.get(`/artifacts/${slug}`);
        setArtifact(res.data);
        
        // Load citations if artifact has citation_ids
        if (res.data.citation_ids?.length > 0) {
          const citRes = await api.get("/citations");
          const linked = citRes.data.filter(c => res.data.citation_ids.includes(c.id));
          setCitations(linked);
        }
        
        // Load related ticket entries
        if (res.data.related_ticket_entries?.length > 0) {
          const tickRes = await api.get("/tickets");
          const linked = tickRes.data.filter(t => res.data.related_ticket_entries.includes(t.id));
          setTicketEntries(linked);
        }
      } catch (err) {
        console.error("Failed to load artifact", err);
      }
      setLoading(false);
    };
    
    loadData();
  }, [slug]);
  
  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }
  
  if (!artifact) {
    return <div className="max-w-4xl mx-auto px-4 py-12"><p>Artifact not found</p></div>;
  }
  
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="artifact-detail-page">
      <Link to="/artifacts" className="text-sm text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center gap-1">
        ← Back to Artifacts
      </Link>
      
      <article className="archive-card p-8">
        <header className="mb-6 pb-6 border-b">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="text-2xl font-bold serif-heading">{artifact.name}</h1>
            <VerificationBadge status={artifact.verification_status} />
          </div>
          
          {artifact.category && (
            <Badge variant="outline" className="mb-4">{artifact.category}</Badge>
          )}
          
          <p className="text-gray-600 leading-relaxed">{artifact.description}</p>
        </header>
        
        <div className="space-y-6">
          {/* Provenance Section */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Provenance
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              {artifact.provenance_public || "Provenance documentation pending"}
            </p>
          </section>
          
          {/* Related Ticket Evidence */}
          {ticketEntries.length > 0 && (
            <section className="bg-blue-50 p-4 rounded border border-blue-100">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-blue-700 mb-3 flex items-center gap-2">
                <Ticket className="w-4 h-4" />
                Related Ticket Evidence
              </h2>
              <div className="space-y-2">
                {ticketEntries.map(entry => (
                  <Link 
                    key={entry.id}
                    to={`/tickets/${entry.section}`}
                    className="block p-3 bg-white rounded border hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-sm">{entry.title}</span>
                        <span className="text-xs text-gray-500 ml-2">({entry.section})</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
          
          {/* Primary Sources */}
          {citations.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Primary Sources
              </h2>
              <CitationList citations={citations} />
            </section>
          )}
          
          {/* Evidence Drawer for Claims */}
          {artifact.claim_ids?.length > 0 && (
            <div className="pt-4 border-t">
              <EvidenceDrawer claimIds={artifact.claim_ids} title={`Evidence: ${artifact.name}`} />
            </div>
          )}
        </div>
        
        <RevisionMeta 
          updatedAt={artifact.updated_at} 
          itemType="artifact" 
          itemId={artifact.id} 
        />
        
        <CiteThis 
          title={artifact.name}
          date={artifact.updated_at}
          url={pageUrl}
        />
      </article>
    </div>
  );
};

export default ArtifactsIndexPage;
